import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { TeamMember } from '../types';

interface TeamSectionProps {
  title: string;
  members: TeamMember[];
}

const TeamSection: React.FC<TeamSectionProps> = ({ title, members }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div ref={ref} className="mb-16">
      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6 }}
        className="text-3xl md:text-4xl font-bold text-penn-blue mb-8"
      >
        {title}
      </motion.h3>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {members.map((member, index) => (
          <TeamMemberCard key={index} member={member} variants={cardVariants} />
        ))}
      </motion.div>
    </div>
  );
};

interface TeamMemberCardProps {
  member: TeamMember;
  variants: any;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({ member, variants }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Extract initials for placeholder
  const getInitials = (name: string) => {
    // Remove credentials (MD, PhD, etc.) and get initials
    const nameWithoutCreds = name.split(',')[0];
    return nameWithoutCreds
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <>
      <motion.div
        variants={variants}
        whileHover={{ y: -5, transition: { duration: 0.3 } }}
        className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-neutral-medium hover:border-penn-red/50"
      >
        <div className="mb-4">
          {/* Square with rounded edges, bigger size */}
          <div className="w-40 h-40 md:w-48 md:h-48 rounded-xl bg-gradient-to-br from-penn-blue to-penn-red flex items-center justify-center mb-4 mx-auto shadow-lg overflow-hidden">
            {member.photo ? (
              <img
                src={member.photo}
                alt={member.name}
                className="w-full h-full object-cover rounded-xl"
              />
            ) : (
              <span className="text-white text-4xl md:text-5xl font-bold">
                {getInitials(member.name)}
              </span>
            )}
          </div>
          <h4 className="text-xl font-bold text-penn-blue text-center mb-2">
            {member.name}
          </h4>
          <p className="text-penn-red font-semibold text-center mb-2">
            {member.role}
          </p>
          {member.startDate && (
            <p className="text-sm text-neutral-dark/70 text-center mb-3">
              {member.startDate}
            </p>
          )}
        </div>

        {/* Read More Button - Always present */}
        <div className="text-center">
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-penn-red text-white px-6 py-2 rounded-lg font-semibold hover:bg-penn-blue transition-colors text-sm"
          >
            Read More
          </button>
        </div>
      </motion.div>

      {/* Modal Popup */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Modal Content */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto z-10"
            >
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-neutral-dark hover:text-penn-red transition-colors z-10"
                aria-label="Close modal"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Modal Body */}
              <div className="p-8">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6">
                  {/* Photo */}
                  <div className="w-40 h-40 md:w-48 md:h-48 rounded-xl bg-gradient-to-br from-penn-blue to-penn-red flex items-center justify-center shadow-lg overflow-hidden flex-shrink-0">
                    {member.photo ? (
                      <img
                        src={member.photo}
                        alt={member.name}
                        className="w-full h-full object-cover rounded-xl"
                      />
                    ) : (
                      <span className="text-white text-5xl font-bold">
                        {getInitials(member.name)}
                      </span>
                    )}
                  </div>

                  {/* Name and Role */}
                  <div className="text-center md:text-left flex-grow">
                    <h3 className="text-3xl font-bold text-penn-blue mb-2">
                      {member.name}
                    </h3>
                    <p className="text-xl text-penn-red font-semibold mb-2">
                      {member.role}
                    </p>
                    {member.startDate && (
                      <p className="text-neutral-dark/70">
                        {member.startDate}
                      </p>
                    )}
                  </div>
                </div>

                {/* Additional Information */}
                {member.research && (
                  <div className="border-t border-neutral-medium pt-6">
                    <h4 className="text-xl font-bold text-penn-blue mb-3">
                      Research Focus
                    </h4>
                    <p className="text-neutral-dark leading-relaxed">
                      {member.research}
                    </p>
                  </div>
                )}

                {/* If no additional info, show a message */}
                {!member.research && !member.startDate && (
                  <div className="border-t border-neutral-medium pt-6">
                    <p className="text-neutral-dark/70 italic">
                      Additional information coming soon.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TeamSection;

